import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { reportsApi } from '../../services/api';
import { funnelSql, lifecycleSql, memberLookupSql, STAGE_C, STAGE_LABEL, clientLabel } from './cohortFunnel';
import GuidedTour, { GuideButton } from './GuidedTour';

// Guided-tour steps — anchored to the data-tour="…" attributes below. Copy only, PII-free.
const FT_TOUR = [
  { target: 'status', tag: 'File Tracker', title: 'Live or fallback', body: "This dot shows whether you're viewing live card-order data (green) or representative sample data, shown only if the backend can't be reached." },
  { target: 'member-lookup', tag: 'File Tracker', title: "Find a member's cards", body: 'Enter a client member id and pick a question to see every card that member has and where each one is. Card status only — no PHI.' },
  { target: 'window', tag: 'File Tracker', title: 'Set the window', body: 'Choose a date range, or use the 7 / 21 / 60 / 90-day presets. Every count and client below reflects this window.' },
  { target: 'metrics', tag: 'File Tracker', title: 'The card-order funnel', body: 'Cards move Sunny requested → Proxy created → FIS embossed → AbCorp produced → Shipped. Each stage is a subset of the one before it.' },
  { target: 'clients', tag: 'File Tracker', title: 'Drill into a client', body: 'Each client is a row. Click one to expand its Card30 files, then open a file to see every card’s lifecycle.' },
  { target: 'reading', tag: 'File Tracker', title: 'What to watch', body: '“Not made” = cards sent to FIS that never got produced — that gap is the reconciliation signal. Later-stage lag is normal in-flight movement.' },
];
const FT_TOUR_KEY = 'printops.ft.tour.v1';

// Print Production — File Tracker (Jake's "file view").
// Each card-issuance file is tracked through 5 phases; a phase is flagged when its
// count diverges from the upstream phase. Read/interpret only, counts + proxies (no PII).
//
// DATA (v1): the SHIPPED phase is LIVE per AB Corp production file (NAF.XML run) from
// ab_corp.workorder_shipping_events. Upstream phases (Received → Printed) aren't yet
// sourceable per file (see memory: file-tracker-lineage-sources), so they show 'na'.
// If the live query is unavailable, the page falls back to representative data, with
// the real classification engine (tone/SLA/status/explained — mirrors file_tracker.py).

const TONE = {
  green:  { bg: '#E1F5EE', fg: '#0F6E56', rail: '#1D9E75', icon: '✓' },
  blue:   { bg: '#E6F1FB', fg: '#185FA5', rail: '#378ADD', icon: '◑' },
  gray:   { bg: '#F1EFE8', fg: '#888780', rail: '#D3D1C7', icon: '○' },
  orange: { bg: '#FAECE7', fg: '#993C1D', rail: '#D85A30', icon: '⇄' },
  red:    { bg: '#FCEBEB', fg: '#A32D2D', rail: '#E24B4A', icon: '!' },
  purple: { bg: '#EEEDFE', fg: '#3C3489', rail: '#7F77DD', icon: '⏸' },
};
const STATUS = {
  ontrack:  { bg: '#E1F5EE', fg: '#0F6E56', label: 'On track',      tone: 'blue' },
  mismatch: { bg: '#FCEBEB', fg: '#A32D2D', label: 'Count mismatch', tone: 'red' },
  risk:     { bg: '#FCEBEB', fg: '#A32D2D', label: 'SLA at risk',    tone: 'red' },
  hold:     { bg: '#EEEDFE', fg: '#3C3489', label: 'On hold',        tone: 'purple' },
};
const PHASES = ['Received', 'Sent to FIS', 'Sent to AB Corp', 'Printed', 'Shipped'];

// Cohort funnel SQL + stage labels live in ./cohortFunnel.js (shared with Ops Health).

const shortCode = (name) => ((name || '').split(/[\s_]+/)[0] || '—').slice(0, 9);

// One tracker row per client — the 5-stage cohort funnel Sunny requested -> Proxy created ->
// FIS embossed -> AbCorp produced -> Shipped. Each downstream stage is a strict subset (monotone):
// Proxy created flags red only if cards were sent but never minted; the rest read as in-flight
// (blue), so normal production/shipping lag isn't mistaken for a count mismatch.
const liveClientFromRow = (r) => {
  const requested = Number(r.requested || 0), created = Number(r.created || 0),
    embossed = Number(r.embossed || 0), produced = Number(r.produced || 0), shipped = Number(r.shipped || 0),
    mbr = Number(r.requested_member || 0), adm = Number(r.requested_admin || 0),
    blk = Number(r.requested_bulk || 0);
  return {
    name: r.tenant_name, label: clientLabel(r.tenant_name), code: shortCode(clientLabel(r.tenant_name)), client: 'Live card-order funnel',
    color: '#378ADD', recs: requested, live: true, funnel: true,
    requested, created, embossed, produced, shipped, mbr, adm, blk,
    steps: [
      { q: requested, st: 'done', solo: true },   // Sunny requested  — FIS_BATCH_SENT (anchor)
      { q: created, st: 'done', tol: true },       // Proxy created    — new proxy minted (flags not-made)
      { q: embossed, st: 'active' },               // FIS embossed     — 'Embossing Complete'
      { q: produced, st: 'active' },               // AbCorp produced  — processing_date
      { q: shipped, st: 'active' },                // Shipped to member — ship_date
    ],
  };
};

const ACTIVE = [
  { name: 'HAP_AEP_20260617.csv', code: 'HAP', client: 'HAP · Medicare Advantage', color: '#378ADD', recs: 34210,
    steps: [{ q: 34210, st: 'done' }, { q: 34210, st: 'done' }, { q: 34210, st: 'done' }, { q: 18400, st: 'active' }, { q: null, st: 'pending' }] },
  { name: 'KP_COMM_20260617.csv', code: 'KP·Comm', client: 'Kaiser Permanente · Commercial', color: '#1D9E75', recs: 8940,
    steps: [{ q: 8940, st: 'done' }, { q: 8940, st: 'done' }, { q: 8936, st: 'done' }, { q: 8936, st: 'active' }, { q: null, st: 'pending' }],
    recon: { tone: 'red', text: 'AB Corp received 8,936 of 8,940 sent by FIS. 4-card gap unexplained — reconciliation open, IM-4471 / CARD30-2188.' } },
  { name: 'MEDICA_M1HP_20260612.csv', code: 'Medica', client: 'Medica · Medicaid M1HP (NEW)', color: '#534AB7', recs: 28750,
    steps: [{ q: 28750, st: 'done' }, { q: 28750, st: 'done' }, { q: null, st: 'hold' }, { q: null, st: 'pending' }, { q: null, st: 'pending' }],
    recon: { tone: 'purple', text: 'Held for MN DHS state review. Release window opens 10/2/2026 — cards must not arrive before then.' } },
  { name: 'KP_RWD_20260613.csv', code: 'KP·Rwd', client: 'Kaiser Permanente · Rewards', color: '#1D9E75', recs: 15300,
    steps: [{ q: 15300, st: 'done' }, { q: 15300, st: 'done' }, { q: null, st: 'overdue' }, { q: null, st: 'pending' }, { q: null, st: 'pending' }],
    recon: { tone: 'red', text: 'AB Corp arrival overdue — T+2 SLA breached. Escalated to VMO (Sunny-side pipeline cleared).' } },
  { name: 'NAV_ASSOC_20260616.csv', code: 'Nav', client: 'Navitus · Associates', color: '#1D9E75', recs: 640,
    steps: [{ q: 640, st: 'done' }, { q: 640, st: 'done' }, { q: 635, st: 'done', exp: true, reason: '5 OFAC holds — cleared with compliance' }, { q: 635, st: 'active' }, { q: null, st: 'pending' }] },
];
const ARCHIVE = [
  { name: 'SUNNYHR_20260617.csv', client: 'Sunny HR · Internal', shipped: 'Jun 17', cards: '120 / 120', outcome: 'Complete', tone: 'green', tracking: '1Z•A4F•21' },
  { name: 'KP_COMM_20260615.csv', client: 'Kaiser Permanente · Commercial', shipped: 'Jun 17', cards: '8,215 / 8,215', outcome: 'Complete · recon resolved (IM-4390)', tone: 'green', tracking: '1Z•7T2•88' },
  { name: 'NAV_ASSOC_20260610.csv', client: 'Navitus · Associates', shipped: 'Jun 12', cards: '635 / 640', outcome: 'Complete · 5 OFAC (explained)', tone: 'orange', tracking: '1Z•99W•04' },
  { name: 'HAP_AEP_20260608.csv', client: 'HAP · Medicare Advantage', shipped: 'Jun 10', cards: '31,980 / 31,980', outcome: 'Complete', tone: 'green', tracking: '1Z•55K•12' },
  { name: 'KP_RWD_20260606.csv', client: 'Kaiser Permanente · Rewards', shipped: 'Jun 9', cards: '14,050 / 14,050', outcome: 'Complete', tone: 'green', tracking: '1Z•31B•77' },
];

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : n);
const prevQ = (s, i) => { for (let j = i - 1; j >= 0; j--) if (typeof s[j].q === 'number') return s[j].q; return null; };
const toneOf = (p, i, s) => {
  if (p.st === 'pending' || p.st === 'na' || p.st === 'await' || p.st === 'tbd') return 'gray';
  if (p.st === 'hold') return 'purple';
  if (p.st === 'overdue') return 'red';
  if (p.st === 'active') return 'blue';
  if (p.solo) return 'green';   // independent windowed count — no cross-stage comparison
  const pv = prevQ(s, i);
  if (pv === null || p.q >= pv) return 'green';
  if (p.exp) return 'orange';
  if (p.tol && (pv - p.q) / pv <= 0.02) return 'green';   // funnel: ≤2% shrink is noise (no absolute floor — a 100% gap on a tiny client must still flag)
  return 'red';
};
const curPhase = (s) => {
  for (let i = 0; i < s.length; i++) if (['active', 'hold', 'overdue'].includes(s[i].st)) return i;
  for (let i = s.length - 1; i >= 0; i--) if (s[i].st === 'done') return i;
  return 0;
};
const slaOf = (recs) => (recs > 30000 ? 'T+4' : 'T+2');
const statusOf = (s) => {
  if (s.some((p) => p.st === 'overdue')) return 'risk';
  for (let i = 0; i < s.length; i++) {
    if (s[i].st === 'done') {
      const pv = prevQ(s, i);
      if (typeof s[i].q === 'number' && pv !== null && s[i].q < pv && !s[i].exp && !s[i].solo
          && !(s[i].tol && (pv - s[i].q) / pv <= 0.02)) return 'mismatch';
    }
  }
  if (s.some((p) => p.st === 'hold')) return 'hold';
  return 'ontrack';
};

const CSS = `
.pft{max-width:100%;width:100%}
.pft-seg{display:inline-flex;background:#f1efe8;border-radius:8px;padding:3px;margin-bottom:1rem}
.pft-seg button{border:none;background:transparent;font-size:13px;padding:5px 14px;border-radius:6px;cursor:pointer;color:#6b6a64;font-family:inherit}
.pft-seg button.on{background:#fff;color:#1a1a18;font-weight:500;box-shadow:0 0 0 .5px rgba(0,0,0,.12)}
.pft-sect{font-size:13px;font-weight:500;color:#6b6a64;margin:0 0 .6rem}
.pft-metrics{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:10px;margin-bottom:1.5rem}
.pft-metric{background:#f1efe8;border-radius:8px;padding:.7rem .85rem}
.pft-metric .ml{font-size:12px;color:#6b6a64;margin:0 0 4px}
.pft-metric .mv{font-size:23px;font-weight:500;margin:0;line-height:1}
.pft-board{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px;margin-bottom:1.75rem}
.pft-bcol{background:#f1efe8;border-radius:8px;padding:8px 6px}
.pft-bcolh{font-size:11px;color:#6b6a64;text-align:center;margin:0 0 8px;min-height:28px;display:flex;align-items:center;justify-content:center}
.pft-bcard{border-radius:8px;padding:6px 7px;margin-bottom:6px;border:.5px solid transparent}
.pft-brow{display:flex;align-items:center;gap:5px}
.pft-cdot{width:10px;height:10px;border-radius:50%;flex:none}
.pft-bqty{font-size:13px;font-weight:500;font-family:ui-monospace,Menlo,monospace;margin-top:3px}
.pft-bempty{font-size:10px;color:#9a988f;text-align:center;padding:4px 0}
.pft-fcard{background:#fff;border:.5px solid rgba(0,0,0,.12);border-radius:12px;padding:1rem 1.1rem;margin-bottom:12px}
.pft-fcard.flag{border:2px solid #e24b4a}
.pft-fhead{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:1.1rem}
.pft-fmeta{display:flex;align-items:center;gap:10px}
.pft-fname{font-size:14px;font-weight:600;font-family:ui-monospace,Menlo,monospace}
.pft-fsub{font-size:12px;color:#6b6a64}
.pft-fright{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.pft-chip{font-size:12px;padding:3px 9px;border-radius:8px;white-space:nowrap;background:#f1efe8;color:#6b6a64}
.pft-track{display:flex;align-items:flex-start}
.pft-step{flex:1;position:relative;text-align:center;padding:18px 2px 6px}
.pft-conn{position:absolute;top:31px;left:-50%;width:100%;height:2px;z-index:0}
.pft-dot{position:relative;z-index:1;width:26px;height:26px;border-radius:50%;margin:0 auto 8px;display:flex;align-items:center;justify-content:center;font-size:13px;color:#fff}
.pft-qty{font-size:15px;font-weight:500}
.pft-plabel{font-size:11px;color:#6b6a64;margin-top:5px}
.pft-delta{display:inline-block;font-size:10px;padding:1px 6px;border-radius:8px;margin-top:6px}
.pft-recon{margin-top:.9rem;font-size:12px;border-radius:8px;padding:8px 10px}
.pft-legend{display:flex;flex-wrap:wrap;gap:14px;margin-top:.4rem;font-size:12px;color:#6b6a64;align-items:center}
.pft-legend span{display:flex;align-items:center;gap:6px}
.pft-lg{width:11px;height:11px;border-radius:50%}
.pft-arow{display:flex;align-items:center;gap:10px;padding:10px 12px;border:.5px solid rgba(0,0,0,.12);border-radius:8px;margin-bottom:8px}
.pft-note{font-size:12px;line-height:1.5;color:#6b6a64;background:#F1EFE8;border-radius:8px;padding:9px 11px;margin:-.2rem 0 1rem}
.pft-note code{font-family:ui-monospace,Menlo,monospace;font-size:11px;background:rgba(0,0,0,.05);padding:1px 4px;border-radius:4px}
.pft-dot-await{background:#fff;border:1.5px dashed #C9C7BD;color:#C9C7BD}
.pft-awaitlbl{display:inline-block;font-size:9px;line-height:1.15;font-style:italic;color:#B4B2A9;text-align:center}
.pft-files{margin-top:.6rem;border-top:.5px dashed rgba(0,0,0,.12);padding-top:.7rem}
.pft-fileshd{font-size:11px;font-weight:500;color:#6b6a64;margin:0 0 .5rem}
.pft-frow{display:flex;align-items:center;gap:9px;padding:6px 0;border-bottom:.5px solid rgba(0,0,0,.06)}
.pft-fname2{font-size:12px;font-family:ui-monospace,Menlo,monospace;color:#1a1a18}
.pft-fmx{margin-left:auto;display:flex;align-items:center;gap:12px;font-size:12px;color:#6b6a64;font-family:ui-monospace,Menlo,monospace}
.pft-caret{font-size:11px;color:#9a988f;margin-left:6px}
.pft-dates{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:0 0 1.1rem;font-size:12px;color:#6b6a64}
.pft-dates input{font:inherit;padding:4px 8px;border:.5px solid rgba(0,0,0,.18);border-radius:6px;background:#fff;color:#1a1a18}
.pft-dates button{font:inherit;font-size:11px;padding:4px 9px;border:.5px solid rgba(0,0,0,.15);border-radius:6px;background:#f1efe8;color:#6b6a64;cursor:pointer}
.pft-dates button.on{background:#fff;color:#1a1a18;font-weight:500;box-shadow:0 0 0 .5px rgba(0,0,0,.12)}
.pft-dlbl{font-weight:500;color:#1a1a18}
`;

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  // These are warehouse DATEs (UTC midnight). Format in UTC so a date like
  // 2026-06-22T00:00:00Z doesn't render as the prior day in a behind-UTC timezone.
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// Per-client drill-down: pull every card ORDER (lifecycleSql), group into Card30 files by send
// day, and let each file expand to its cards — one row per card showing the stages it has
// reached and its current stage. Grain = order, so multiple replacements for one member lay
// out cleanly as separate rows. PII-free: FIS order id + card proxy + dates only.
const FT_PAGE_SIZE = 500;

function ClientFiles({ tenant, start, end }) {
  const [openFile, setOpenFile] = React.useState(null);
  const q = useInfiniteQuery({
    queryKey: ['ftLifecycle', tenant, start, end],
    // Keyset-paginated so a client's full order history is always reachable with no cap, and so
    // pages stay stable even though the window includes today — each page asks for orders before
    // the last req_id seen (not an OFFSET, which a newly-arrived order would shift and duplicate).
    queryFn: ({ pageParam = null }) =>
      reportsApi.executeCustomQuery(lifecycleSql(tenant, start, end, FT_PAGE_SIZE, pageParam)),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      const page = lastPage?.data || [];
      return page.length === FT_PAGE_SIZE ? page[page.length - 1].req_id : undefined;
    },
    retry: false,
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(
    () => (q.data?.pages || []).flatMap((p) => p?.data || []),
    [q.data]
  );
  const files = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const k = r.file_tag || '—';
      let g = m.get(k);
      if (!g) { g = { tag: k, cards: [], requested: 0, created: 0, embossed: 0, produced: 0, shipped: 0 }; m.set(k, g); }
      g.cards.push(r); g.requested += 1;
      if (r.proxy) g.created += 1;
      if (r.embossed_at) g.embossed += 1;
      if (r.produced_at) g.produced += 1;
      if (r.shipped_at) g.shipped += 1;
    }
    return Array.from(m.values());
  }, [rows]);

  return (
    <div className="pft-files">
      <p className="pft-fileshd">Card30 order files — expand to see each card&apos;s lifecycle</p>
      {q.isPlaceholderData && q.isFetching && (
        <div style={{ background: '#FAF3E0', color: '#8a6d1a', borderRadius: 6, padding: '6px 10px', fontSize: 12, margin: '4px 0 10px' }}>
          Updating for the new window — the orders below are from the previous window until this finishes.
        </div>
      )}
      {rows.length > 0 && (q.hasNextPage || q.isFetchingNextPage) && (
        <div style={{ background: '#F1EFE8', color: '#6b6a64', borderRadius: 6, padding: '6px 10px', fontSize: 12, margin: '4px 0 10px' }}>
          Showing {fmt(rows.length)} card orders so far — this client has more in this window.
        </div>
      )}
      {q.isLoading && <div className="pft-fsub">Loading…</div>}
      {q.isError && (
        <div className="pft-fsub" style={{ color: '#A32D2D' }}>
          {rows.length
            ? 'Couldn’t load more orders — the query failed, so this list may be incomplete.'
            : 'Couldn’t load this client’s card orders — the query failed. This is not an empty window; retry shortly.'}
        </div>
      )}
      {!q.isLoading && !q.isError && files.length === 0 && <div className="pft-fsub">No card orders in this window.</div>}
      {files.map((g) => {
        const open = openFile === g.tag;
        const inTransit = Math.max(g.embossed - g.shipped, 0);
        const notMade = Math.max(g.requested - g.created, 0);
        return (
          <div key={g.tag}>
            <div className="pft-frow" style={{ cursor: 'pointer' }} role="button" tabIndex={0}
              aria-expanded={open}
              onClick={() => setOpenFile(open ? null : g.tag)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenFile(open ? null : g.tag); } }}>
              <span className="pft-cdot" style={{ background: '#1D9E75' }} />
              <span className="pft-fname2">{g.tag}<span className="pft-caret">{open ? '▾ hide cards' : '▸ ' + g.cards.length + ' cards'}</span></span>
              <span className="pft-fmx">
                <span>{fmt(g.requested)} requested</span>
                <span>{fmt(g.embossed)} embossed</span>
                <span>{fmt(g.shipped)} shipped</span>
                {inTransit > 0 && <span style={{ color: '#185FA5' }}>{fmt(inTransit)} in transit</span>}
                {notMade > 0 && <span style={{ color: '#A32D2D' }}>{fmt(notMade)} not made</span>}
              </span>
            </div>
            {open && (
              <div style={{ overflowX: 'auto', margin: '2px 0 10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>
                    {['FIS order', 'Card proxy', 'By', 'Sunny requested', 'Proxy created', 'FIS embossed', 'AbCorp produced', 'Shipped to member', 'Stage'].map((h, i) => (
                      <th key={h} style={{ textAlign: i > 2 ? 'right' : 'left', color: '#6b6a64', fontWeight: 600,
                        padding: '5px 8px', borderBottom: '.5px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {g.cards.map((c) => {
                      const sc = STAGE_C[c.current_stage] || STAGE_C.Requested;
                      return (
                        <tr key={c.req_id}>
                          <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace,Menlo,monospace' }}>{c.fis_order_id || '—'}</td>
                          <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace,Menlo,monospace' }}>{c.proxy || '—'}</td>
                          <td style={{ padding: '5px 8px' }}>{c.ordered_by}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b6a64' }}>{fmtDate(c.requested_at)}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b6a64' }}>{c.created_at ? fmtDate(c.created_at) : '—'}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b6a64' }}>{c.embossed_at ? fmtDate(c.embossed_at) : '—'}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b6a64' }}>{c.produced_at ? fmtDate(c.produced_at) : '—'}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b6a64' }}>{c.shipped_at ? fmtDate(c.shipped_at) : '—'}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                            <span style={{ background: sc.bg, color: sc.fg, padding: '1px 7px', borderRadius: 7, fontSize: 11 }}>{STAGE_LABEL[c.current_stage] || c.current_stage}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
      {q.hasNextPage && (
        <button
          type="button"
          onClick={() => q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
          style={{ background: '#fff', border: '.5px solid #D3D1C7', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', margin: '4px 0' }}
        >
          {q.isFetchingNextPage ? 'Loading more…' : 'Load more orders'}
        </button>
      )}
    </div>
  );
}

const iso = (d) => d.toISOString().slice(0, 10);
const defaultRange = () => {
  const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 21);
  return { start: iso(start), end: iso(end) };
};

// Fixed questions for the member card lookup — a small, auditable set (not free-form chat).
const LOOKUP_Q = [
  { key: 'where', label: 'Where is the card now?' },
  { key: 'shipped', label: 'Has it shipped?' },
  { key: 'count', label: 'How many card orders?' },
  { key: 'stuck', label: 'Anything stuck?' },
];
// null for a missing/invalid date (NOT 0) so callers can tell "age unknown" from "aged 0 days".
// new Date(null) is the 1970 epoch, which would read as ~20,000 days old, so the guard matters.
const daysSince = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : Math.floor((Date.now() - d.getTime()) / 86400000);
};

// Member card lookup (inside the File Tracker) — PII-free card STATUS by CLIENT MEMBER ID.
// Maps member_id -> consumer_code -> the cohort lifecycle (memberLookupSql). Output is card
// status only: Card30 file, FIS order, proxy, per-stage dates, current stage — NO name, DOB,
// address, PAN, or tracking. Fixed questions only; the answer is framed from the same result set.
function MemberLookup() {
  const [memberId, setMemberId] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [q, setQ] = useState('where');
  const res = useQuery({
    queryKey: ['memberCardLookup', submitted],
    queryFn: () => reportsApi.executeCustomQuery(memberLookupSql(submitted)),
    enabled: !!submitted,
    retry: false,
  });
  const cards = res.data?.data || [];
  const ask = (key) => { setQ(key); const id = memberId.trim(); if (id) setSubmitted(id); };
  const seq = (c) => Number(c.card_seq || 1);
  const orderCount = new Set(cards.map((c) => c.fis_order_id)).size;
  const reissues = cards.filter((c) => seq(c) > 1).length;
  // "current" card = the most recently minted one
  const current = cards.length ? [...cards].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0] : null;

  const answer = () => {
    if (!submitted) return null;
    if (res.isLoading) return 'Looking up…';
    if (res.isError) return 'Lookup failed — check the connection and try again.';
    if (cards.length === 0) return `No cards found for “${submitted}” — no order yet, or not a client member id.`;
    const shipped = cards.filter((c) => c.shipped_at).length;
    const stage = STAGE_LABEL[current.current_stage] || current.current_stage;
    const extra = reissues > 0 ? ` This member has ${cards.length} cards (${reissues} reissue${reissues === 1 ? '' : 's'}).` : '';
    if (q === 'shipped') {
      return shipped === cards.length
        ? `Yes — ${cards.length === 1 ? 'the card' : `all ${cards.length} cards`} shipped.${extra}`
        : `${shipped} of ${cards.length} card${cards.length === 1 ? '' : 's'} shipped. The current card is at ${stage}${current.shipped_at ? '' : ' — in transit'}.${extra}`;
    }
    if (q === 'count') {
      return `${cards.length} card${cards.length === 1 ? '' : 's'} across ${orderCount} order${orderCount === 1 ? '' : 's'}${reissues > 0 ? ` (${reissues} reissue${reissues === 1 ? '' : 's'})` : ''}. Current card ${current.requested_at ? `requested ${fmtDate(current.requested_at)}` : '(a reissue)'}, now ${stage}.`;
    }
    if (q === 'stuck') {
      // Age from created_at (the proxy-mint date, present on every card including reissues), NOT
      // requested_at — which memberLookupSql returns NULL for reissues, so measuring from it would
      // flag every unshipped reissue as overdue, even one minted today.
      const stuck = cards.filter((c) => { const age = daysSince(c.created_at); return !c.shipped_at && age != null && age > 10; });
      return stuck.length
        ? `${stuck.length} card${stuck.length === 1 ? '' : 's'} still unshipped 10+ days after being made — review.`
        : 'No cards stuck — all progressing or shipped.';
    }
    return `Current card (…${String(current.proxy).slice(-4)}) is at ${stage}${current.shipped_at ? ` — shipped ${fmtDate(current.shipped_at)}` : current.embossed_at ? ` — embossed ${fmtDate(current.embossed_at)}, in transit` : ''}.${extra}`;
  };

  const cell = { padding: '5px 8px', textAlign: 'right', color: '#6b6a64', whiteSpace: 'nowrap' };
  return (
    <div style={{ border: '.5px solid rgba(0,0,0,.12)', borderRadius: 12, padding: '.9rem 1rem', marginBottom: '1.1rem', background: '#fff' }}>
      <p className="pft-sect" style={{ margin: '0 0 .5rem' }}>Member card lookup
        <span style={{ fontWeight: 400, color: '#9a988f' }}> · by client member id</span></p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: '.6rem' }}>
        <input value={memberId} onChange={(e) => setMemberId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ask(q)} placeholder="client member id"
          style={{ font: 'inherit', fontSize: 13, padding: '5px 10px', border: '.5px solid rgba(0,0,0,.2)', borderRadius: 6, minWidth: 200 }} />
        {LOOKUP_Q.map((x) => (
          <button key={x.key} onClick={() => ask(x.key)} disabled={!memberId.trim()} className="pft-chip"
            style={{ cursor: memberId.trim() ? 'pointer' : 'default', border: 'none',
              background: q === x.key && submitted ? '#E6F1FB' : '#f1efe8',
              color: q === x.key && submitted ? '#185FA5' : '#6b6a64' }}>{x.label}</button>
        ))}
      </div>
      {submitted && <div className="pft-note" style={{ margin: '0 0 .6rem' }}>{answer()}</div>}
      {cards.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>
              {['Client', 'Card', 'FIS order', 'By', 'Sunny requested', 'Proxy created', 'FIS embossed', 'AbCorp produced', 'Shipped to member', 'Stage', 'Card proxy'].map((h, i) => (
                <th key={h} style={{ textAlign: i > 3 && i < 9 ? 'right' : 'left', color: '#6b6a64', fontWeight: 600,
                  padding: '5px 8px', borderBottom: '.5px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {cards.map((c) => {
                const sc = STAGE_C[c.current_stage] || STAGE_C.Requested;
                const reissue = seq(c) > 1;
                return (
                  <tr key={String(c.fis_order_id) + '-' + String(c.proxy)}>
                    <td style={{ padding: '5px 8px' }}>{clientLabel(c.tenant_name)}</td>
                    <td style={{ padding: '5px 8px' }}>
                      {reissue
                        ? <span style={{ background: '#FAECE7', color: '#993C1D', padding: '1px 7px', borderRadius: 7, fontSize: 11 }}>reissue</span>
                        : <span style={{ color: '#9a988f', fontSize: 11 }}>original</span>}
                    </td>
                    <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace,Menlo,monospace' }}>{c.fis_order_id}</td>
                    <td style={{ padding: '5px 8px' }}>{c.ordered_by}</td>
                    <td style={cell}>{c.requested_at ? fmtDate(c.requested_at) : '—'}</td>
                    <td style={cell}>{c.created_at ? fmtDate(c.created_at) : '—'}</td>
                    <td style={cell}>{c.embossed_at ? fmtDate(c.embossed_at) : '—'}</td>
                    <td style={cell}>{c.produced_at ? fmtDate(c.produced_at) : '—'}</td>
                    <td style={cell}>{c.shipped_at ? fmtDate(c.shipped_at) : '—'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                      <span style={{ background: sc.bg, color: sc.fg, padding: '1px 7px', borderRadius: 7, fontSize: 11 }}>{STAGE_LABEL[c.current_stage] || c.current_stage}</span>
                    </td>
                    <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace,Menlo,monospace' }}>{c.proxy || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function PrintOpsFileTracker() {
  const [tab, setTab] = useState('active');
  const [openClient, setOpenClient] = useState(null);
  const [range, setRange] = useState(defaultRange);
  const [tourOpen, setTourOpen] = useState(false);
  const [autoDone, setAutoDone] = useState(false);

  // An inverted window (start after end) runs a BETWEEN that returns no rows; min/max on the inputs
  // mark typed values invalid but don't block onChange, so guard it here and skip the query.
  const invalidRange = !!range.start && !!range.end && range.start > range.end;

  // Live client funnel for the selected window. retry:false → if unavailable
  // (local-dev / backend down), liveRows is empty and the page falls back to
  // representative data immediately. Query re-runs when the date range changes.
  const { data: funnelResp, isLoading: funnelLoading, isError: funnelError,
    isFetching: funnelFetching, isPlaceholderData: funnelPlaceholder } = useQuery({
    queryKey: ['ftClientFunnel', range.start, range.end],
    queryFn: () => reportsApi.executeCustomQuery(funnelSql(range.start, range.end)),
    enabled: !invalidRange,
    retry: false,
    placeholderData: keepPreviousData,   // hold the prior window's rows while a new window loads
  });
  const liveRows = funnelResp?.data || [];
  const isLive = liveRows.length > 0;
  // While a newly-changed window loads, keepPreviousData keeps the prior rows on screen (isLive
  // stays true). Track that so the status chip says "updating" rather than presenting the old
  // counts as the current window's — and so a failed refresh isn't hidden behind a green "Live".
  const stale = funnelFetching && funnelPlaceholder;
  // Representative sample data is a LOCAL-DEV fallback ONLY — shown when the backend is genuinely
  // unreachable while developing, never in any deployed build. Gating on import.meta.env.DEV (true
  // only under `vite dev`) means a production SQL/warehouse error surfaces the failure state below
  // instead of ever swapping in fabricated card files. funnelError alone can't tell a down backend
  // from a failed query, so the env gate is what guarantees fabricated data never reaches prod.
  const demo = !isLive && funnelError && import.meta.env.DEV;

  // First-visit auto-start: run the tour once, but only after there's content so the highlighted
  // anchors exist. Remembered in localStorage; the Guide button reopens it anytime.
  useEffect(() => {
    if (autoDone || !(isLive || demo)) return;
    setAutoDone(true);
    try { if (!localStorage.getItem(FT_TOUR_KEY)) setTourOpen(true); } catch (e) { /* ignore */ }
  }, [isLive, demo, autoDone]);

  const phases = isLive
    ? [STAGE_LABEL.Requested, STAGE_LABEL.Created, STAGE_LABEL.Embossed, STAGE_LABEL.Produced, STAGE_LABEL.Shipped]
    : PHASES;

  const live = useMemo(() => {
    if (!isLive) return null;
    return { active: liveRows.map(liveClientFromRow), archive: [] };
  }, [isLive, liveRows]);

  const files = useMemo(() => {
    const src = isLive ? live.active : (demo ? ACTIVE : []);
    return src.map((f) => {
      const status = statusOf(f.steps);
      return { ...f, sla: slaOf(f.recs), status, flag: ['mismatch', 'risk'].includes(status) };
    });
    // `demo` must be a dep: on an initial query failure it flips false->true while isLive/live
    // are unchanged, so without it this memo keeps the empty [] and the page shows "Representative
    // data" over an empty list.
  }, [isLive, live, demo]);

  const archiveList = isLive ? live.archive : ARCHIVE;

  const metrics = useMemo(() => {
    if (isLive) {
      const requested = files.reduce((s, f) => s + (f.requested || 0), 0);
      const created = files.reduce((s, f) => s + (f.created || 0), 0);
      const embossed = files.reduce((s, f) => s + (f.embossed || 0), 0);
      const produced = files.reduce((s, f) => s + (f.produced || 0), 0);
      const shipped = files.reduce((s, f) => s + (f.shipped || 0), 0);
      const notMade = requested - created;
      return { cards: [
        ['Sunny requested', requested],
        ['Proxy created', created],
        ['FIS embossed', embossed],
        ['AbCorp produced', produced],
        ['Shipped to member', shipped],
        ...(notMade > 0 ? [['Not made', notMade, '#a32d2d']] : []),
      ] };
    }
    return { cards: [
      ['Active files', files.length],
      ['Shipped today', ARCHIVE.filter((a) => a.shipped === 'Jun 17').length],
      ['Count mismatch', files.filter((f) => f.status === 'mismatch').length, files.some((f) => f.status === 'mismatch') ? '#a32d2d' : ''],
      ['SLA at risk', files.filter((f) => f.status === 'risk').length, files.some((f) => f.status === 'risk') ? '#a32d2d' : ''],
      ['On hold', files.filter((f) => f.status === 'hold').length],
    ] };
  }, [isLive, files, live]);

  const days = Math.round((new Date(range.end) - new Date(range.start)) / 86400000);
  const preset = (n) => {
    const end = new Date(); const start = new Date(); start.setDate(start.getDate() - n);
    setRange({ start: iso(start), end: iso(end) });
  };

  // Live-status chip (dot colour + label). Non-success states are checked BEFORE "Live" so that a
  // failed refresh or an in-flight window change can't sit behind a green "Live" label while
  // keepPreviousData is still showing the prior window's counts.
  const statusMeta = () => {
    if (invalidRange) return { dot: '#E24B4A', text: 'Invalid date range' };
    if (demo) return { dot: '#D3A93A', text: 'Representative data' };
    if (funnelError) return { dot: '#E24B4A', text: isLive ? 'Refresh failed · showing last loaded window' : 'Query failed' };
    if (stale) return { dot: '#D3A93A', text: 'Updating…' };
    if (isLive) return { dot: '#1D9E75', text: 'Live · card-order funnel' };
    if (funnelLoading) return { dot: '#C9C7BD', text: 'Loading…' };
    return { dot: '#C9C7BD', text: 'No orders in this window' };
  };
  const status = statusMeta();

  // Message for the no-content state — an inverted window or a failed query is called out as such
  // (not as an empty window), so operators never mistake a warehouse outage for "nothing to show".
  const emptyIsError = invalidRange || funnelError;
  const emptyMessage = () => {
    if (invalidRange) return 'The start date is after the end date — adjust the window to load card orders.';
    if (funnelLoading) return 'Loading live card-order data…';
    if (funnelError) return 'Couldn’t load card-order data — the query failed. This is not an empty window; retry shortly.';
    return 'No card orders for this window.';
  };

  return (
    <div className="pft">
      <style>{CSS}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '.75rem' }}>
        <h2 style={{ fontSize: 19, fontWeight: 600, margin: 0 }}>Print Production — File Tracker</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span data-tour="status" style={{ fontSize: 12, color: '#6b6a64', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: status.dot }} />
            {status.text}
          </span>
          <GuideButton onClick={() => setTourOpen(true)} />
        </div>
      </div>

      {isLive && (
        <div className="pft-note" data-tour="reading">
          Card-order funnel by client, {days}-day window. Each card is followed from the FIS order through embossing,
          production and shipment; stages are cumulative, so &ldquo;Not made&rdquo; (requested without a proxy) is the
          reconciliation signal. Expand a client for its Card30 files and per-card lifecycle.
        </div>
      )}

      <div data-tour="member-lookup"><MemberLookup /></div>

      <div className="pft-dates" data-tour="window">
        <span className="pft-dlbl">Window</span>
        <input type="date" value={range.start} max={range.end}
          onChange={(e) => e.target.value && setRange((r) => ({ ...r, start: e.target.value }))} />
        <span>–</span>
        <input type="date" value={range.end} min={range.start}
          onChange={(e) => e.target.value && setRange((r) => ({ ...r, end: e.target.value }))} />
        {[7, 21, 60, 90].map((n) => (
          <button key={n} className={days === n ? 'on' : ''} onClick={() => preset(n)}>{n}d</button>
        ))}
      </div>

      {!isLive && !demo && (
        <div className="pft-note" style={{ background: 'transparent', padding: '1.5rem 0', color: emptyIsError ? '#A32D2D' : '#9a988f' }}>
          {emptyMessage()}
        </div>
      )}

      {(isLive || demo) && (
      <div className="pft-seg">
        <button className={tab === 'active' ? 'on' : ''} onClick={() => setTab('active')}>{isLive ? 'Clients' : 'Active'} {files.length}</button>
        {!isLive && <button className={tab === 'past' ? 'on' : ''} onClick={() => setTab('past')}>Past files · archive</button>}
      </div>
      )}

      {(isLive || demo) && tab === 'active' && (
        <>
          <div className="pft-metrics" data-tour="metrics">
            {metrics.cards.map(([l, v, c], i) => (
              <div className="pft-metric" key={i}><p className="ml">{l}</p><p className="mv" style={{ color: c || 'inherit' }}>{typeof v === 'number' ? fmt(v) : v}</p></div>
            ))}
          </div>

          {!isLive && <>
          <p className="pft-sect">Pipeline board — where each file sits now</p>
          <div className="pft-board" style={{ gridTemplateColumns: `repeat(${phases.length}, minmax(0, 1fr))` }}>
            {phases.map((ph, ci) => {
              const inCol = files.filter((f) => curPhase(f.steps) === ci);
              const T0 = STATUS;
              return (
                <div className="pft-bcol" key={ci}>
                  <p className="pft-bcolh">{ph}</p>
                  {inCol.length === 0 && <div className="pft-bempty">{ci === phases.length - 1 ? '→ archive' : '—'}</div>}
                  {inCol.map((f) => {
                    const T = TONE[T0[f.status].tone]; const cp = f.steps[ci];
                    return (
                      <div className="pft-bcard" key={f.name} style={{ background: T.bg, borderColor: T.rail }} title={f.label || f.name}>
                        <div className="pft-brow"><span className="pft-cdot" style={{ background: f.color }} /><span style={{ fontSize: 11, fontWeight: 600, color: T.fg }}>{f.code}</span></div>
                        <div className="pft-bqty" style={{ color: T.fg }}>{cp && typeof cp.q === 'number' ? fmt(cp.q) : '—'}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          </>}

          <p className="pft-sect" data-tour="clients">{isLive ? 'Client funnel — counts by phase' : 'File detail — counts by phase'}</p>
          {files.map((f) => {
            const st = STATUS[f.status];
            return (
              <div className={`pft-fcard${f.flag ? ' flag' : ''}`} key={f.name}>
                <div className="pft-fhead" style={f.funnel ? { cursor: 'pointer' } : undefined}
                  role={f.funnel ? 'button' : undefined}
                  tabIndex={f.funnel ? 0 : undefined}
                  aria-expanded={f.funnel ? openClient === f.name : undefined}
                  onClick={f.funnel ? () => setOpenClient(openClient === f.name ? null : f.name) : undefined}
                  onKeyDown={f.funnel ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenClient(openClient === f.name ? null : f.name); } } : undefined}>
                  <div className="pft-fmeta"><span className="pft-cdot" style={{ background: f.color }} />
                    <div><div className="pft-fname">{f.label || f.name}{f.funnel && <span className="pft-caret">{openClient === f.name ? '▾ hide files' : '▸ files'}</span>}</div><div className="pft-fsub">{f.client}</div></div></div>
                  <div className="pft-fright">
                    <span className="pft-chip" style={{ fontFamily: 'ui-monospace,Menlo,monospace' }}>{fmt(f.recs)} {f.funnel ? 'requested' : 'cards'}</span>
                    {f.funnel && (f.mbr > 0 || f.adm > 0 || f.blk > 0) && <span className="pft-chip">{fmt(f.mbr)} mbr · {fmt(f.adm)} adm{f.blk > 0 ? ` · ${fmt(f.blk)} bulk` : ''}</span>}
                    {!f.funnel && <span className="pft-chip">SLA {f.sla}</span>}
                    {!f.live && <span className="pft-chip" style={{ background: st.bg, color: st.fg }}>{st.label}</span>}
                  </div>
                </div>
                <div className="pft-track">
                  {f.steps.map((p, i) => {
                    const T = TONE[toneOf(p, i, f.steps)]; const pv = prevQ(f.steps, i);
                    const tbd = p.st === 'await' || p.st === 'tbd';
                    const drop = typeof p.q === 'number' && p.st !== 'active' && !p.solo && pv !== null && p.q < pv
                      && !(p.tol && (pv - p.q) / pv <= 0.02);
                    return (
                      <div className="pft-step" key={i}>
                        {i > 0 && <div className="pft-conn" style={{ background: tbd ? '#E2E0D7' : T.rail }} />}
                        {tbd
                          ? <div className="pft-dot pft-dot-await">·</div>
                          : <div className="pft-dot" style={{ background: T.rail }}>{T.icon}</div>}
                        {p.st === 'tbd' ? <span className="pft-awaitlbl">TBD</span>
                          : p.st === 'await' ? <span className="pft-awaitlbl">awaiting<br />source</span>
                          : p.st === 'na' ? <span style={{ color: '#9a988f', fontSize: 11 }}>n/a</span>
                          : p.q === null ? <span style={{ color: '#9a988f' }}>—</span>
                          : <span className="pft-qty">{fmt(p.q)}</span>}
                        <div className="pft-plabel">{phases[i]}</div>
                        {drop && <div className="pft-delta" style={{ background: T.bg, color: T.fg }}>{p.q - pv}{p.exp ? ' · ' + (p.reason || 'expected') : ''}</div>}
                        {p.st === 'overdue' && <div className="pft-delta" style={{ background: TONE.red.bg, color: TONE.red.fg }}>overdue</div>}
                      </div>
                    );
                  })}
                </div>
                {f.recon && <div className="pft-recon" style={{ background: TONE[f.recon.tone].bg, color: TONE[f.recon.tone].fg }}>{f.recon.text}</div>}
                {f.funnel && openClient === f.name && <ClientFiles tenant={f.name} start={range.start} end={range.end} />}
              </div>
            );
          })}

          <div className="pft-legend">
            <span style={{ fontWeight: 500, color: '#1a1a18' }}>Cool:</span>
            <span><span className="pft-lg" style={{ background: '#1D9E75' }} />Match</span>
            <span><span className="pft-lg" style={{ background: '#378ADD' }} />In progress</span>
            <span><span className="pft-lg" style={{ background: '#B4B2A9' }} />Pending</span>
            <span style={{ fontWeight: 500, color: '#1a1a18' }}>Warm:</span>
            <span><span className="pft-lg" style={{ background: '#D85A30' }} />Variance (explained)</span>
            <span><span className="pft-lg" style={{ background: '#E24B4A' }} />Variance (unexplained)</span>
            <span><span className="pft-lg" style={{ background: '#7F77DD' }} />On hold</span>
            {isLive && <span><span className="pft-lg" style={{ background: '#378ADD' }} />In-flight subset (as-of)</span>}
          </div>
        </>
      )}

      {(isLive || demo) && tab === 'past' && (
        <>
          <p className="pft-sect">Past files — shipped &amp; reconciled</p>
          {archiveList.map((a) => {
            const T = TONE[a.tone];
            return (
              <div className="pft-arow" key={a.name}>
                <span className="pft-cdot" style={{ background: T.rail }} />
                <div><div className="pft-fname">{a.name}</div><div className="pft-fsub">{a.client}</div></div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <span className="pft-chip" style={{ background: T.bg, color: T.fg }}>{a.outcome}</span>
                  <div className="pft-fsub" style={{ fontFamily: 'ui-monospace,Menlo,monospace' }}>{a.cards} · shipped {a.shipped} · {a.tracking}</div>
                </div>
              </div>
            );
          })}
        </>
      )}

      <GuidedTour steps={FT_TOUR} open={tourOpen} onClose={() => setTourOpen(false)} storageKey={FT_TOUR_KEY} />
    </div>
  );
}
